import { useMemo } from 'react';
import type { FunctionComponent } from 'react';

import type { BaseProps, ForwardProps, TestIdProp } from '../../../types';
import Select, { Option } from '../../Select';
import type { SelectProps } from '../../Select/Select';
import type { FormControlProps } from '../../FormControl';
import { useConfiguration, useTestIds } from '../../../hooks';
import { getDayOfWeekInputTestIds } from '../DateTime.test-ids';
import { createClassName, withTestIds } from '../../../utils';

import { CALENDAR_TYPES, type CalendarType } from './local';
import { getCalendarTypeFromLocale, getDayOfWeekNamesMap } from './utils';

export type DayOfWeek = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface DayOfWeekInputProps extends TestIdProp, BaseProps {
  /**
   * Number from range [1-7].
   * Creates a controlled input and sets the value. Requires an onChange handler to update value.
   * value + onChange is the recommended method per React team.
   */
  value?: DayOfWeek;
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: FormControlProps['id'];
  /** Set visual state based on a validation state. */
  status?: FormControlProps['status'];
  /** Pass a string or a fragment with an Icon and string. */
  label?: FormControlProps['label'];
  /** Visually hides the label region. */
  labelHidden?: FormControlProps['labelHidden'];
  /** It is recommended to pass a simple string to offer guidance. Text will be styled based on status prop. */
  info?: FormControlProps['info'];
  /** Indicate if the field is required. The browser defaults to false. */
  required?: FormControlProps['required'];
  /** Disable the control. The browser defaults to false. */
  disabled?: FormControlProps['disabled'];
  /** Makes the input non editable and non clickable. The browser defaults to false. */
  readOnly?: FormControlProps['readOnly'];
  /** Sets html name attribute for the underlying control. Useful for mapping to a data field. */
  name?: FormControlProps['name'];
  /** Pass a heading and content to show additional information on the field. */
  additionalInfo?: FormControlProps['additionalInfo'];
  /** Callback fired when input value changes. */
  onChange?: (value: DayOfWeek) => void;
}

// returns index of the first day of week according to given calendar type
// returned value is a position of the day in base ISO calendar
function getFirstWeekDayForCalendar(refCalendarType: CalendarType = CALENDAR_TYPES.ISO_8601) {
  switch (refCalendarType) {
    case CALENDAR_TYPES.ISO_8601:
      // start week with Monday
      return 0;
    case CALENDAR_TYPES.ARABIC:
      // start week with Saturday
      return 5;
    case CALENDAR_TYPES.HEBREW:
    case CALENDAR_TYPES.US:
      // start week with Sunday
      return 6;
    default:
      // ISO as default
      return 0;
  }
}

// returns array of Options for day names, ordered according to given calendar
function generateOrderedDays(
  dayOfWeekNames: { [key: number]: string },
  refCalendarType: CalendarType
) {
  const optionsArray = Object.keys(dayOfWeekNames).map(key => {
    return (
      <Option key={dayOfWeekNames[Number(key)]} value={key.toString()}>
        {dayOfWeekNames[Number(key)]}
      </Option>
    );
  });

  const startingDayIndex = getFirstWeekDayForCalendar(refCalendarType);

  return [...optionsArray.slice(startingDayIndex), ...optionsArray.slice(0, startingDayIndex)];
}

const DayOfWeekInput: FunctionComponent<DayOfWeekInputProps & ForwardProps> = (
  props: DayOfWeekInputProps
) => {
  const { locale } = useConfiguration();
  const { testId, value, required, readOnly, disabled, onChange, className, status, ...restProps } =
    props;

  const testIds = useTestIds(testId, getDayOfWeekInputTestIds);

  // days ordered according to given calendar
  const dayOfWeekOptions = useMemo(
    () => generateOrderedDays(getDayOfWeekNamesMap(locale), getCalendarTypeFromLocale(locale)),
    [locale]
  );

  const onChangeWrapper: SelectProps['onChange'] = ev => {
    onChange?.(Number(ev.target.value) as DayOfWeek);
  };

  return (
    <Select
      data-testid={testIds.control}
      required={required}
      value={value ? value.toString() : undefined}
      onChange={onChangeWrapper}
      readOnly={readOnly}
      disabled={disabled || readOnly}
      status={status}
      {...restProps}
      className={createClassName('day-of-week-input', className, { status })}
    >
      {!required && <Option> </Option>}
      {dayOfWeekOptions}
    </Select>
  );
};

export default withTestIds(DayOfWeekInput, getDayOfWeekInputTestIds);
